import type { Vec2, Vec3 } from '../../geo/types';

/**
 * Where the vertices of a patterned face lie on it, in metres, when its builder gives it no uv of
 * its own (STYLE §15): aFacade.xy carry them and aFacade.zw the face's width and height.
 */
export interface FaceCoords {
  readonly uv: readonly Vec2[];
  readonly extent: readonly [number, number];
}

export interface QuadCoords extends FaceCoords {
  readonly uv: readonly [Vec2, Vec2, Vec2, Vec2];
}

/* Normals come from cross products of exact corner differences, so a true wall or floor meets these
   to the last bit; anything tilted further is a slope. */
const LEVEL = 1e-6;
const UPRIGHT = 1e-6;

function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

/* Rightward as seen from outside and up the face; a level face has no rightward of its own, so it
   takes the frame's x axis. */
function faceAxes(normal: Vec3, frameX: Vec3): readonly [Vec3, Vec3] {
  const across = Math.hypot(normal[0], normal[2]);
  const right: Vec3 = across < LEVEL ? frameX : [normal[2] / across, 0, -normal[0] / across];
  return [right, cross(normal, right)];
}

/**
 * A planar face's coordinates from its world corners and unit normal: u rightward from its left
 * end, as seen from outside, so a quoin or closer knows the face's ends; v up it. A wall takes v at
 * world height, so its courses run level around the building and on up the stages and gables
 * stacked on it; any other face takes v from its lowest point, so a roof's courses start at its
 * eave. A level face runs u along the frame's x axis and v to the frame's left.
 */
export function planarFaceCoords(points: readonly Vec3[], normal: Vec3, frameX: Vec3): FaceCoords {
  const [right, up] = faceAxes(normal, frameX);
  const upright = Math.abs(normal[1]) < UPRIGHT;
  const us = points.map((p) => dot(p, right));
  const vs = points.map((p) => (upright ? p[1] : dot(p, up)));
  const [u0, v0] = [Math.min(...us), Math.min(...vs)];
  return {
    uv: points.map((_, i): Vec2 => [us[i]! - u0, upright ? vs[i]! : vs[i]! - v0]),
    extent: [Math.max(...us) - u0, Math.max(...vs) - v0],
  };
}

/**
 * One side quad of MeshBuilder.cylinder, corners (bottom i, top i, top i + 1, bottom i + 1): u the
 * arc length around the side, rightward as seen from outside, so it runs on unbroken from quad to
 * quad but for the one seam at the ring's first point; v the slant up the side. A round face has no
 * ends, so its width is 0.
 */
export function cylinderSideCoords(
  segment: number,
  segments: number,
  radiusBottom: number,
  radiusTop: number,
  height: number
): QuadCoords {
  const around = (i: number, radius: number): number => (1 - i / segments) * 2 * Math.PI * radius;
  const slant = Math.hypot(height, radiusBottom - radiusTop);
  const next = segment + 1;
  return {
    uv: [
      [around(segment, radiusBottom), 0],
      [around(segment, radiusTop), slant],
      [around(next, radiusTop), slant],
      [around(next, radiusBottom), 0],
    ],
    extent: [0, slant],
  };
}
